import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import api from '../services/api.ts';
import FileUpload, { FileAttachment } from '../components/FileUpload.tsx';
import { formatCurrencyInput, parseCurrency, applyCurrencyMask } from '../utils/currency.ts';
import {
  ArrowLeftIcon,
  TruckIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

// Types
interface VehicleFormData {
  acquisitionValue: number;
  currentValue: number;
  acquisitionDate: string;
  attachments: FileAttachment[];
  details: {
    make: string;
    model: string;
    year: number;
    licensePlate: string;
    color: string;
    notes?: string;
  };
}

const EditVehicle: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<VehicleFormData>({
    acquisitionValue: 0,
    currentValue: 0,
    acquisitionDate: new Date().toISOString().split('T')[0],
    attachments: [],
    details: {
      make: '',
      model: '',
      year: new Date().getFullYear(),
      licensePlate: '',
      color: '',
      notes: '',
    },
  });

  // Estados para os valores formatados dos inputs
  const [acquisitionValueDisplay, setAcquisitionValueDisplay] = useState<string>('');
  const [currentValueDisplay, setCurrentValueDisplay] = useState<string>('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const vehicleCategories = [
    { value: 'crlv', label: 'CRLV' },
    { value: 'manual', label: 'Manual' },
    { value: 'nota_fiscal', label: 'Nota Fiscal' },
    { value: 'fotos', label: 'Fotos' },
    { value: 'outros', label: 'Outros' },
  ];

  // Query para buscar os dados do veículo
  const { data: vehicle, isLoading: isLoadingVehicle } = useQuery(
    ['vehicle', id],
    async () => {
      const response = await api.get(`/vehicles/${id}`);
      return response.data;
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      staleTime: 1000,
      cacheTime: 5 * 60 * 1000,
      onSuccess: data => {
        // Converter attachments do backend para o formato esperado pelo FileUpload
        const convertedAttachments: FileAttachment[] = (data.attachments || []).map(
          (attachment: any) => ({
            id: attachment.id || `existing-${Date.now()}-${Math.random()}`,
            name: attachment.name || attachment.originalName || 'Arquivo',
            type: attachment.mimetype?.startsWith('image/') ? 'image' : 'document',
            category: attachment.category || 'outros',
            file: null,
            preview: attachment.mimetype?.startsWith('image/')
              ? getBackendUrl(attachment.url)
              : undefined,
            url: attachment.url ? getBackendUrl(attachment.url) : undefined,
            size: attachment.size || 0,
            mimetype: attachment.mimetype,
          })
        );

        setFormData({
          acquisitionValue: data.purchasePrice || 0,
          currentValue: data.sellingPrice || 0,
          acquisitionDate: data.purchaseDate
            ? data.purchaseDate.split('T')[0]
            : new Date().toISOString().split('T')[0],
          attachments: convertedAttachments,
          details: {
            make: data.make || '',
            model: data.model || '',
            year: data.year || new Date().getFullYear(),
            licensePlate: data.licensePlate || '',
            color: data.color || '',
            notes: data.notes || '',
          },
        });

        // Definir valores formatados
        setAcquisitionValueDisplay(formatCurrencyInput(data.purchasePrice || 0));
        setCurrentValueDisplay(formatCurrencyInput(data.sellingPrice || 0));
      },
    }
  );

  // Mutation para atualizar veículo
  const updateVehicleMutation = useMutation(
    async (data: VehicleFormData) => {
      let uploadedFiles: any[] = [];

      // Primeiro, fazer upload dos arquivos novos se existirem
      const newFiles = data.attachments.filter(att => att.file && !att.url);
      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach(attachment => {
          if (attachment.file) {
            formData.append('files', attachment.file);
            formData.append('categories', attachment.category || 'outros');
          }
        });

        const uploadResponse = await api.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (uploadResponse.data.success && uploadResponse.data.data?.files) {
          uploadedFiles = uploadResponse.data.data.files.map((file: any, index: number) => ({
            name: file.originalName,
            url: file.url,
            size: file.size,
            mimetype: file.mimetype,
            category: newFiles[index].category || 'outros',
            type: newFiles[index].type,
          }));
        }
      }

      // Combinar arquivos existentes com os novos
      const existingFiles = data.attachments.filter(att => att.url && !att.file);
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const allAttachments = [
        ...existingFiles.map(att => ({
          name: att.name,
          url: att.url?.replace(baseUrl, ''),
          category: att.category,
          mimetype: att.mimetype || (att.type === 'image' ? 'image/jpeg' : 'application/pdf'),
          size: att.size,
          type: att.type,
        })),
        ...uploadedFiles,
      ];

      // Preparar dados do veículo
      const vehicleData = {
        make: data.details.make,
        model: data.details.model,
        year: data.details.year,
        licensePlate: data.details.licensePlate,
        color: data.details.color,
        purchasePrice: data.acquisitionValue,
        sellingPrice: data.currentValue || null,
        purchaseDate: data.acquisitionDate,
        notes: data.details.notes,
        attachments: allAttachments,
      };

      const response = await api.put(`/vehicles/${id}`, vehicleData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vehicle', id]);
        queryClient.invalidateQueries('vehicles');
        navigate('/vehicles');
      },
      onError: (error: any) => {
        console.error('Erro ao atualizar veículo:', error);
      },
    }
  );

  const handleInputChange = (field: keyof VehicleFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleDetailsChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        [field]: value,
      },
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Função específica para lidar com campos de valor monetário
  const handleCurrencyChange = (field: 'acquisitionValue' | 'currentValue', value: string) => {
    const maskedValue = applyCurrencyMask(value);
    const numericValue = parseCurrency(maskedValue);

    if (field === 'acquisitionValue') {
      setAcquisitionValueDisplay(maskedValue);
    } else {
      setCurrentValueDisplay(maskedValue);
    }

    setFormData(prev => ({
      ...prev,
      [field]: numericValue,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.acquisitionValue <= 0) {
      newErrors.acquisitionValue = 'Valor de aquisição deve ser maior que zero';
    }

    // if (formData.currentValue <= 0) {
    //   newErrors.currentValue = 'Valor de venda deve ser maior que zero';
    // }

    if (!formData.acquisitionDate) {
      newErrors.acquisitionDate = 'Data de aquisição é obrigatória';
    }

    // Validações específicas para veículos
    if (!formData.details.make?.trim()) {
      newErrors.make = 'Marca é obrigatória';
    }

    if (!formData.details.model?.trim()) {
      newErrors.model = 'Modelo é obrigatório';
    }

    if (
      !formData.details.year ||
      formData.details.year < 1900 ||
      formData.details.year > new Date().getFullYear() + 1
    ) {
      newErrors.year = 'Ano deve estar entre 1900 e ' + (new Date().getFullYear() + 1);
    }

    if (!formData.details.licensePlate?.trim()) {
      newErrors.licensePlate = 'Placa é obrigatória';
    }

    if (!formData.details.color?.trim()) {
      newErrors.color = 'Cor é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    updateVehicleMutation.mutate(formData);
  };

  if (isLoadingVehicle) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/vehicles')}
          className="flex items-center text-primary-600 hover:text-primary-700 mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Voltar para Veículos
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Editar Veículo</h1>
        <p className="text-gray-600 mt-2">Atualize as informações do seu veículo</p>
      </div>

      <div className="space-y-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              <TruckIcon className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Informações Básicas</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="acquisitionDate"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Data de Aquisição *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="acquisitionDate"
                    type="date"
                    value={formData.acquisitionDate}
                    onChange={e => handleInputChange('acquisitionDate', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.acquisitionDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.acquisitionDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.acquisitionDate}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="acquisitionValue"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor de Aquisição *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="acquisitionValue"
                    type="text"
                    value={acquisitionValueDisplay}
                    onChange={e => handleCurrencyChange('acquisitionValue', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.acquisitionValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0,00"
                  />
                </div>
                {errors.acquisitionValue && (
                  <p className="mt-1 text-sm text-red-600">{errors.acquisitionValue}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="currentValue"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor de Venda
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="currentValue"
                    type="text"
                    value={currentValueDisplay}
                    onChange={e => handleCurrencyChange('currentValue', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.currentValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0,00"
                  />
                </div>
                {errors.currentValue && (
                  <p className="mt-1 text-sm text-red-600">{errors.currentValue}</p>
                )}
              </div>
            </div>
          </div>

          {/* Detalhes do Veículo */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              <TruckIcon className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Detalhes do Veículo</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="make" className="block text-sm font-medium text-gray-700 mb-2">
                  Marca *
                </label>
                <input
                  id="make"
                  type="text"
                  value={formData.details.make}
                  onChange={e => handleDetailsChange('make', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.make ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Toyota, Honda, Ford"
                />
                {errors.make && <p className="mt-1 text-sm text-red-600">{errors.make}</p>}
              </div>

              <div>
                <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">
                  Modelo *
                </label>
                <input
                  id="model"
                  type="text"
                  value={formData.details.model}
                  onChange={e => handleDetailsChange('model', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.model ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Corolla, Civic, Focus"
                />
                {errors.model && <p className="mt-1 text-sm text-red-600">{errors.model}</p>}
              </div>

              <div>
                <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                  Ano *
                </label>
                <input
                  id="year"
                  type="number"
                  value={formData.details.year}
                  onChange={e => handleDetailsChange('year', parseInt(e.target.value) || 0)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.year ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="2023"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                />
                {errors.year && <p className="mt-1 text-sm text-red-600">{errors.year}</p>}
              </div>

              <div>
                <label
                  htmlFor="licensePlate"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Placa *
                </label>
                <input
                  id="licensePlate"
                  type="text"
                  value={formData.details.licensePlate}
                  onChange={e => handleDetailsChange('licensePlate', e.target.value.toUpperCase())}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.licensePlate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="ABC-1234"
                />
                {errors.licensePlate && (
                  <p className="mt-1 text-sm text-red-600">{errors.licensePlate}</p>
                )}
              </div>

              <div>
                <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-2">
                  Cor *
                </label>
                <input
                  id="color"
                  type="text"
                  value={formData.details.color}
                  onChange={e => handleDetailsChange('color', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.color ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Branco, Preto, Prata"
                />
                {errors.color && <p className="mt-1 text-sm text-red-600">{errors.color}</p>}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Observações
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  value={formData.details.notes || ''}
                  onChange={e => handleDetailsChange('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Informações adicionais sobre o veículo..."
                />
              </div>
            </div>
          </div>

          {/* Anexos - Fotos e Documentos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Fotos e Documentos</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Adicione fotos do veículo e documentos como documento do carro (CRLV), nota fiscal, manual, etc.
            </p>

            <FileUpload
              files={formData.attachments}
              onFilesChange={files => setFormData(prev => ({ ...prev, attachments: files }))}
              maxFiles={20}
              maxSizePerFile={10}
              showCategories={true}
              categories={vehicleCategories}
            />
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/vehicles')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={updateVehicleMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {updateVehicleMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Atualizando...
                </>
              ) : (
                'Atualizar Veículo'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditVehicle;
